import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { NotifiableStore } from '../../../../../../../../ErrorActionableReaction';
import { error as makeError } from '../../../../../../../../ErrorHandling';
import { errorAlert, FlashAlert } from '../../../../../../../../Notifications/Types';
import {
  SaveActionsState,
  confirmingCancel as toConfirmingCancelState,
  confirmingCreate as toConfirmingCreateState,
  confirmingPublish as toConfirmingPublishState,
  error as toErrorState,
  publishing as toPublishingState,
  ready as toReadyState,
  saving as toSavingState,
} from './Types';

class SaveActionsStore implements NotifiableStore {
  @observable
  state: SaveActionsState = toReadyState();

  constructor() {
    makeObservable(this);
  }

  @computed
  get notification(): Maybe<FlashAlert> {
    if (this.state.kind === 'error') {
      return just(errorAlert(makeError('Your request failed.')));
    }
    return nothing();
  }

  @action
  saving = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = toSavingState();
        break;
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  publishing = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = toPublishingState();
        break;
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = () => {
    switch (this.state.kind) {
      case 'saving':
      case 'publishing':
      case 'error':
        this.state = toReadyState();
        break;
      case 'ready':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (err: Error) => {
    switch (this.state.kind) {
      case 'saving':
      case 'publishing':
        this.state = toErrorState(err);
        break;
      case 'ready':
      case 'error':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  showPublishConfirmation = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = toConfirmingPublishState();
        break;
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  showCreateConfirmation = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = toConfirmingCreateState();
        break;
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  cancelConfirmation = () => {
    switch (this.state.kind) {
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        this.state = toReadyState();
        break;
      case 'ready':
      case 'saving':
      case 'publishing':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  confirmPublish = () => {
    switch (this.state.kind) {
      case 'confirming-publish':
        this.state = toPublishingState();
        break;
      case 'ready':
      case 'saving':
      case 'publishing':
      case 'confirming-create':
      case 'confirming-cancel':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  confirmCreate = () => {
    switch (this.state.kind) {
      case 'confirming-create':
        this.state = toSavingState();
        break;
      case 'ready':
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-cancel':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  showCancelConfirmation = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = toConfirmingCancelState();
        break;
      case 'saving':
      case 'publishing':
      case 'confirming-publish':
      case 'confirming-create':
      case 'confirming-cancel':
        break;
      default:
        assertNever(this.state);
    }
  };
}

export default SaveActionsStore;
